import React, { useEffect, useState } from "react";
import SideMenu from "../../../components/sideMenu";
import DropdownInput from "../../../components/dropdownInput";
import Loading from "../../../components/loading";
import { organizationService } from "../../../services/organizationService";
import { roleService } from "../../../services/roleService";
import { Organization, RoleCapability, RoleMatrix } from "../../../type";
import { FaCircleArrowLeft } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

const RoleAdminPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgIdx, setSelectedOrgIdx] = useState<number>(0);

  const [capabilities, setCapabilities] = useState<RoleCapability[]>([]);
  const [roleMatrix, setRoleMatrix] = useState<RoleMatrix>({});

  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const [orgRes, capRes] = await Promise.all([
          organizationService.getOrganizations({}),
          roleService.getCapabilities(),
        ]);

        const orgs: Organization[] = orgRes.data || [];
        const caps: RoleCapability[] = capRes.data || [];

        setOrganizations(orgs);
        setCapabilities(caps);

        if (orgs.length > 0) {
          setSelectedOrgIdx(0);
          const firstOrgId = orgs[0].id;
          const matrixRes = await roleService.getRoleMatrix(firstOrgId);
          setRoleMatrix(matrixRes.permissions || {});
        }
      } catch (err) {
        console.error("[RoleAdminPage] init error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  const onSelectOrg = async (idx: number) => {
    setSelectedOrgIdx(idx);
    const org = organizations[idx];
    if (!org) return;

    setIsLoading(true);
    try {
      const res = await roleService.getRoleMatrix(org.id);
      setRoleMatrix(res.permissions || {});
    } catch (err) {
      console.error("[RoleAdminPage] getRoleMatrix error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePermission = (
    capKey: string,
    roleKey: "user" | "admin" | "super_admin"
  ) => {
    setRoleMatrix((prev) => ({
      ...prev,
      [capKey]: {
        user: prev[capKey]?.user ?? false,
        admin: prev[capKey]?.admin ?? false,
        super_admin: prev[capKey]?.super_admin ?? false,
        [roleKey]: !prev[capKey]?.[roleKey],
      },
    }));
  };

  const handleSave = async () => {
    const org = organizations[selectedOrgIdx];
    if (!org) return;

    setSaving(true);
    try {
      await roleService.updateRoleMatrix(org.id, roleMatrix);
      alert("Role permissions saved.");
    } catch (err) {
      console.error("[RoleAdminPage] updateRoleMatrix error:", err);
      alert("Failed to save role permissions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SideMenu active={3}>
      <div className="relative">
        {isLoading && <Loading />}
        <div className="flex">
          {/* Main content */}
          <div className="container p-4 pt-8 xl:pt-4">
            <div className="flex md:flex-row flex-col gap-4 items-center justify-between mb-4">
              <div>
                <div className="flex gap-4 items-center">
                  <FaCircleArrowLeft
                    className="text-2xl cursor-pointer"
                    onClick={() => navigate(-1)}
                  />
                  <h3 className="text-xl font-bold">Role Permissions</h3>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Configure CRUD permissions (Create, Edit, Delete, View) for
                  each role within an organization.
                </p>
              </div>

              {organizations.length > 0 && (
                <div className="w-full md:w-64">
                  <DropdownInput
                    options={organizations.map((o) => o.orgName)}
                    value_idx={selectedOrgIdx}
                    onSelect={onSelectOrg}
                  />
                </div>
              )}
            </div>

            {/* Table: capabilities x roles */}
            <div className="border rounded-2xl bg-white overflow-hidden text-sm">
              <div className="grid grid-cols-5 bg-navy text-white p-3 font-semibold">
                <div className="col-span-2">Permission</div>
                <div className="text-center">User</div>
                <div className="text-center">Admin</div>
                <div className="text-center">Super Admin</div>
              </div>

              {capabilities.map((cap, idx) => {
                const row = roleMatrix[cap.key] || {
                  user: false,
                  admin: false,
                  super_admin: false,
                };

                return (
                  <div
                    key={cap.key}
                    className={`grid grid-cols-5 items-center p-3 ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <div className="col-span-2">
                      <p className="font-medium">{cap.label}</p>
                      {cap.description && (
                        <p className="text-xs text-gray-500">
                          {cap.description}
                        </p>
                      )}
                    </div>

                    {(["user", "admin", "super_admin"] as const).map((r) => (
                      <div key={r} className="flex justify-center">
                        <input
                          type="checkbox"
                          checked={row[r]}
                          className="accent-green"
                          disabled={saving}
                          onChange={() => togglePermission(cap.key, r)}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}

              {capabilities.length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  No permissions defined.
                </div>
              )}
            </div>

            {/* Save button */}
            <div className="flex justify-end mt-4">
              <button
                className="px-4 py-2 rounded-lg bg-navy text-white text-sm"
                onClick={handleSave}
                disabled={saving || organizations.length === 0}
              >
                {saving ? "Saving..." : "Save Permissions"}
              </button>
            </div>

            <p className="mt-4 text-xs text-gray-500">
              *Only the super admin of this organization can configure these
              permissions (and the platform super admin can override all
              organizations). Organization creation and deletion are restricted
              to platform super admins and are not controlled by this matrix.
            </p>
          </div>
        </div>
      </div>
    </SideMenu>
  );
};

export default RoleAdminPage;
